import { useEffect, useRef, useState } from 'react';
import { useAnalyticsStore } from '~contexts/mobx';
import { SOURCES } from './sources';
import type { KpiCardDataRequest, KpiCardState, KpiSourceId } from './types';

/**
 * Fetches one card's data through its source, holding per-card state locally
 * (each card has its own window, so a shared store observable would not scale).
 * Re-fetches when the resolved request changes; ignores stale resolutions.
 *
 * Pacing lives in the store (`AnalyticsStore.fetchKpiEnvelope`), below the
 * envelope dedup map, so every standardized card is throttled uniformly with no
 * per-page wiring. This is unconditional and app-wide: every page that renders
 * standardized cards (the RPP dashboard, the CT/CK analytics Overview) is paced,
 * and so is any non-card caller of the envelope (e.g. the Taxi Driver campaign
 * page's direct `fetchKpiEnvelope` call for campaign-purchases), not just pages
 * that opt in. Only pages that mount `KpiLoadProgressBar` show the load bar.
 * This hook just fetches and wires the result into state.
 *
 * `waveKey` (optional) is the page-level filter identity. When supplied, the hook
 * opens the store's load wave on each fetch and passes the fetch's AbortSignal
 * through, so a filter change restarts the bar and drops or aborts the stale wave's
 * requests. It is read through a ref, never an effect dep: a card that pins its
 * period must not re-fetch just because the page filter (and thus `waveKey`)
 * changed. Pages that omit it keep today's behaviour (paced, never wave-reset).
 *
 * `enabled` (default true) gates the fetch: when false the hook holds `loading`
 * and never calls the source. A card whose request is not yet fully resolved (the
 * coupon card awaiting its default coupon id) passes `false` until ready, so no
 * request fires without the selection it needs. Every existing caller omits it
 * and is unchanged.
 *
 * `req.live` computes now; `onLiveSettled` fires when it ends or is dropped, so the
 * caller resets `live`, which alone never re-fetches. It holds only for the inputs
 * it was clicked on: if they change first, the card fetches normally.
 */
export const useKpiCardData = (
    sourceId: KpiSourceId,
    req: KpiCardDataRequest,
    waveKey?: string,
    enabled = true,
    onLiveSettled?: () => void
): KpiCardState => {
    const analyticsStore = useAnalyticsStore();
    const [state, setState] = useState<KpiCardState>({ status: 'loading' });

    // Read the latest waveKey without making it an effect dep (see above).
    const waveKeyRef = useRef(waveKey);
    waveKeyRef.current = waveKey;
    const onLiveSettledRef = useRef(onLiveSettled);
    onLiveSettledRef.current = onLiveSettled;
    const answeredRef = useRef<
        Pick<KpiCardDataRequest, 'period' | 'compare'>
    >();
    const lastRunRef = useRef<{ identity: string; live: boolean }>();
    const liveClickRef = useRef<string | null>();

    // Primitive deps so the effect only re-runs on a real change.
    const regionKey = req.region.join(',');

    // Everything that decides WHAT is fetched, `live` aside.
    const identity = JSON.stringify([
        sourceId,
        req.form,
        req.metric,
        req.period,
        req.region,
        req.compare,
        req.breakdownBy,
        req.breakdownBaseMetricId,
        req.limit,
        req.palette,
        req.promotionId,
        req.campaignId,
        req.segment,
        req.campaignToDate,
        req.campaignRunsToday,
        req.sample,
        enabled,
    ]);

    // `null`: the inputs moved since the click, which is then spent even if they
    // come back.
    if (req.live !== true) {
        liveClickRef.current = undefined;
    } else if (liveClickRef.current === undefined) {
        liveClickRef.current = identity;
    } else if (liveClickRef.current !== identity) {
        liveClickRef.current = null;
    }
    const live = req.live === true && liveClickRef.current === identity;

    useEffect(() => {
        const previous = lastRunRef.current;
        lastRunRef.current = { identity, live };
        const liveDropped = req.live === true && !live;
        if (liveDropped) onLiveSettledRef.current?.();

        // `live` going back to false after a live answer, nothing else changed:
        // the live figures stay, no second request.
        if (
            previous?.live &&
            !live &&
            !liveDropped &&
            previous.identity === identity
        ) {
            return;
        }

        // Not ready to fetch yet (e.g. a coupon card before its default coupon
        // resolves): hold loading; a later `enabled` re-runs this effect.
        if (!enabled) {
            setState({ status: 'loading' });
            return;
        }

        const controller = new AbortController();
        let cancelled = false;
        // Keep the last ready data through consecutive re-fetches (a `prev`
        // already `loading` carries its `stale` forward).
        const answered = answeredRef.current;
        setState((prev) => {
            const stale =
                prev.status === 'ready' && answered
                    ? {
                          data: prev.data,
                          caveats: prev.caveats,
                          computedAt: prev.computedAt,
                          served: prev.served,
                          ...answered,
                      }
                    : prev.status === 'loading' || prev.status === 'error'
                    ? prev.stale
                    : undefined;
            return stale ? { status: 'loading', stale } : { status: 'loading' };
        });

        // A filter change re-runs this effect (period/region deps changed): open
        // the new wave before fetching, so the bar restarts and the store can
        // drop the old wave's requests as their signals abort below.
        if (waveKeyRef.current !== undefined) {
            analyticsStore.ensureKpiWave(waveKeyRef.current);
        }

        // A failed or degraded live answer keeps the figure it was replacing,
        // shown beside the error.
        const fail = (otherwise: KpiCardState = { status: 'error' }) =>
            setState((prev) =>
                live && prev.status === 'loading' && prev.stale
                    ? { status: 'error', stale: prev.stale }
                    : otherwise
            );

        SOURCES[sourceId]
            .getCardData(liveDropped ? { ...req, live: false } : req, {
                analyticsStore,
                signal: controller.signal,
            })
            .then((next) => {
                // Ignore a resolution for a request the card has moved on from
                // (unmount, or a filter change that re-ran this effect).
                if (cancelled) return;
                answeredRef.current = {
                    period: req.period,
                    compare: req.compare,
                };
                if (
                    live &&
                    (next.status === 'error' || next.status === 'unavailable')
                ) {
                    fail(next);
                } else {
                    setState(next);
                }
                if (live) onLiveSettledRef.current?.();
            })
            .catch(() => {
                // An abandoned request rejects here, but by then this
                // effect has re-run (cancelled === true), so it is ignored like
                // any other stale resolution.
                if (cancelled) return;
                fail();
                if (live) onLiveSettledRef.current?.();
            });

        return () => {
            cancelled = true;
            // Let the store abandon this card's request if no other card wants it.
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        analyticsStore,
        sourceId,
        req.form,
        req.metric,
        req.period.start,
        req.period.end,
        req.period.preset?.period,
        regionKey,
        req.compare?.range.start,
        req.compare?.range.end,
        req.compare?.label,
        req.breakdownBy,
        req.breakdownBaseMetricId,
        req.limit,
        req.palette,
        req.promotionId,
        req.campaignId,
        // Listed so a group switch re-runs the effect: that is what aborts the
        // outgoing request and marks its resolution stale, so a slow answer for
        // the group the reader left cannot land on the card.
        req.segment,
        req.campaignToDate?.start,
        req.campaignToDate?.end,
        req.campaignRunsToday,
        // `sample` (placeholder cards) and `palette` (pie cards) are threaded into
        // the request; today both come from a static module-level spec so their
        // identity is stable and this changes no runtime behavior, but listing them
        // means a future dynamic sample/palette correctly triggers a re-fetch.
        req.sample,
        enabled,
        live,
    ]);

    return state;
};
